import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Calendar, ListChecks, LogOut, Mail, Moon, Settings, Sun, Users } from 'lucide-react';
import { Mailbox } from './Mailbox.jsx';
import { CalendarPage } from './Calendar.jsx';
import { ContactsPage } from './Contacts.jsx';
import { TasksPage } from './Tasks.jsx';
import { SettingsPage } from './Settings.jsx';
import { Compose } from '../components/Compose.jsx';
import { useToast } from '../components/ui.jsx';

const APPS = [['mail', 'Mail', Mail], ['calendar', 'Calendar', Calendar], ['contacts', 'Contacts', Users], ['tasks', 'Tasks', ListChecks]];

/** Authenticated shell: icon rail + current app + a persistent docked composer. */
export function Shell({ session, theme, onLogout }) {
  const flash = useToast();
  const [app, setApp] = useState('mail');
  const [intent, setIntent] = useState(null); // { type: 'openMail', emailId }
  const [badges, setBadges] = useState({});
  const [compose, setCompose] = useState(null); // initial draft object, or null
  const [composeMin, setComposeMin] = useState(false);
  const [contacts, setContacts] = useState([]); // for recipient autocomplete + auto-add
  const [mailTick, setMailTick] = useState(0);   // bump to refresh the mailbox after a send
  const initial = (session.name || session.email)[0].toUpperCase();

  const loadContacts = useCallback(() => { session.apis?.contacts?.list().then(setContacts).catch(() => {}); }, [session]);
  useEffect(() => { loadContacts(); }, [loadContacts]);

  // Auto-add a sender/recipient to contacts the first time we see them (on reading
  // a message), so contacts populate without manual work and feed autocomplete.
  const seenRef = useRef(new Set());
  const noteContact = useCallback((email, name) => {
    if (!email || !email.includes('@') || email.toLowerCase() === (session.email || '').toLowerCase()) return;
    const lk = email.toLowerCase();
    if (seenRef.current.has(lk)) return; seenRef.current.add(lk);
    if (contacts.some((c) => (c.email || '').toLowerCase() === lk)) return;
    session.apis?.contacts?.create({ name: name || email.split('@')[0], email, title: '', company: '', phone: '', group: 'external', fav: false }).then(loadContacts).catch(() => {});
  }, [contacts, session, loadContacts]);

  const navigate = (to, i) => { setApp(to); setIntent(i || null); };
  // Open the docked composer - shared by the Compose button, reply/forward, and
  // "message a contact". The composer lives here (not inside Mailbox) so it
  // persists while you switch between mail / calendar / contacts / tasks.
  const openCompose = useCallback((draft = {}) => { setCompose(draft); setComposeMin(false); }, []);

  return (
    <div className="km-shell">
      <aside className="km-rail">
        <img src="/brand/mark.svg" width="32" height="32" alt="KryptaMail" style={{ marginBottom: 14 }} />
        {APPS.map(([id, name, Icon]) => (
          <button key={id} className={`km-rail-btn ${app === id ? 'active' : ''}`} title={name} onClick={() => navigate(id)}>
            <Icon size={19} />{badges[id] ? <span className="km-rail-dot" /> : null}
          </button>
        ))}
        <div className="km-rail-bottom">
          <button className="km-rail-btn" title={theme.isDark ? 'Switch to light' : 'Switch to dark'} onClick={theme.toggle}>{theme.isDark ? <Sun size={18} /> : <Moon size={18} />}</button>
          <button className={`km-rail-btn ${app === 'settings' ? 'active' : ''}`} title="Settings" onClick={() => navigate('settings')}><Settings size={18} /></button>
          <button className="km-rail-btn" title="Sign out" onClick={onLogout}><LogOut size={18} /></button>
          <div className="km-rail-avatar" title={session.email}>{initial}</div>
        </div>
      </aside>
      {app === 'mail' && <Mailbox session={session} intent={intent} refreshSignal={mailTick} onCompose={openCompose} onContactSeen={noteContact} onBadge={(n) => setBadges((b) => ({ ...b, mail: n }))} />}
      {app === 'calendar' && <CalendarPage session={session} />}
      {app === 'contacts' && <ContactsPage session={session} onCompose={(to) => openCompose({ to: [to] })} onOpenMail={(id) => navigate('mail', { type: 'openMail', emailId: id })} />}
      {app === 'tasks' && <TasksPage session={session} onOpenMail={(id) => navigate('mail', { type: 'openMail', emailId: id })} onBadge={(n) => setBadges((b) => ({ ...b, tasks: n }))} />}
      {app === 'settings' && <SettingsPage session={session} theme={theme} />}

      {compose && (
        <Compose
          session={session}
          initial={compose}
          contacts={contacts}
          dark={theme.isDark}
          flash={flash}
          minimized={composeMin}
          onMinimize={() => setComposeMin(true)}
          onRestore={() => setComposeMin(false)}
          onClose={() => { setCompose(null); setComposeMin(false); setMailTick((t) => t + 1); }}
          onSent={(enc) => { setCompose(null); setComposeMin(false); flash(enc ? 'Encrypted and sent' : 'Sent'); loadContacts(); setMailTick((t) => t + 1); }}
        />
      )}
    </div>
  );
}
